// Phase 10B "AutoMind 2050" — Admin.
//
// Production controls and operational tools, grouped into tabs.
// All API calls and role gates are preserved verbatim.

import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { PageHeader } from '../../components/ui/page-header';
import { TabBar } from '../../components/ui/tab-bar';
import { DataCard } from '../../components/ui/data-card';
import { EmptyState } from '../../components/ui/empty-state';

import AdminSheetsSyncWidget from '../../components/AdminSheetsSyncWidget';
import { adminApi, leadsApi, campaignsApi, callsApi, apiFetch } from '../../api';
import ProductionReadinessCard from './ProductionReadinessCard';
import DatabaseStatsGrid from './DatabaseStatsGrid';
import CallReconciliationCard from './CallReconciliationCard';
import DataCleanupCard from './DataCleanupCard';
import DNCListManagementCard from './DNCListManagementCard';
import ComplianceConfigCard from './ComplianceConfigCard';
import QuickTestCallCard from './QuickTestCallCard';
import DealersCard from './DealersCard';
import UsersCard from './UsersCard';
import AgentTemplatesCard from './agent-templates/AgentTemplatesCard';
import { useAuth } from '../../auth/AuthContext';

const Admin = () => {
  const { hasRole } = useAuth();
  const isPlatformAdmin = hasRole('platform_admin');
  const canManageUsers = hasRole('platform_admin', 'dealer_owner');
  const [productionStatus, setProductionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [executing, setExecuting] = useState(null);
  const [tab, setTab] = useState('production');

  const [placingCall, setPlacingCall] = useState(false);
  const [lastCallStatus, setLastCallStatus] = useState(null);
  const [isPollingStatus, setIsPollingStatus] = useState(false);

  useEffect(() => {
    if (isPlatformAdmin) fetchProductionStatus();
    else setLoading(false);
  }, [isPlatformAdmin]);

  const fetchProductionStatus = async () => {
    try {
      setLoading(true);
      setErrored(false);
      const data = await adminApi.productionStatus();
      setProductionStatus(data);
    } catch (err) {
      console.error('Error fetching status:', err);
      setErrored(true);
      toast.error('Failed to fetch production status');
    } finally {
      setLoading(false);
    }
  };

  const handleReconcileCalls = async () => {
    try {
      setExecuting('reconcile');
      const result = await adminApi.reconcileCalls();
      toast.success(`Call Reconciliation Complete - Updated: ${result.reconciled?.updated || 0}, Stale Fixed: ${result.stale_cleaned || 0}`);
      await fetchProductionStatus();
    } catch (err) {
      console.error('Error reconciling calls:', err);
      toast.error('Failed to reconcile calls');
    } finally {
      setExecuting(null);
    }
  };

  const handleCleanupTestData = async (dryRun = true) => {
    if (!dryRun) {
      // eslint-disable-next-line no-restricted-globals
      const confirmed = confirm(
        'WARNING: This will permanently delete all test data!\n\n' +
        'This action cannot be undone. Make sure you have reviewed the dry-run results first.\n\n' +
        'Are you absolutely sure you want to proceed?'
      );
      if (!confirmed) return;
    }

    try {
      setExecuting(dryRun ? 'cleanup-dry' : 'cleanup-live');
      const result = await adminApi.cleanupTestData(dryRun);

      if (dryRun) {
        const deleteCount = Object.values(result.deleted || {}).reduce((a, b) => a + b, 0);
        toast.info(`Dry Run Complete - Would delete ${deleteCount} test records. Review and run live cleanup if correct.`);
      } else {
        const deleteCount = Object.values(result.deleted || {}).reduce((a, b) => a + b, 0);
        toast.success(`Cleanup Complete - Deleted ${deleteCount} test records successfully`);
      }

      await fetchProductionStatus();
    } catch (err) {
      console.error('Error cleaning data:', err);
      toast.error('Failed to clean test data');
    } finally {
      setExecuting(null);
    }
  };

  const handleRefreshDNC = async (file) => {
    try {
      setExecuting('dnc');
      const formData = new FormData();
      formData.append('file', file);

      const result = await apiFetch('/api/admin/refresh-dnc-list', {
        method: 'POST',
        body: formData,
      });

      if (result.success) {
        toast.success(`DNC List Updated - Loaded ${result.count} phone numbers`);
      } else {
        toast.error(result.message || 'Failed to update DNC list');
      }

      await fetchProductionStatus();
    } catch (err) {
      console.error('Error updating DNC:', err);
      toast.error('Failed to update DNC list');
    } finally {
      setExecuting(null);
    }
  };

  const pollCallStatus = async (callId) => {
    if (!callId) return;

    setIsPollingStatus(true);
    let attempts = 0;
    const maxAttempts = 12;

    const poll = async () => {
      try {
        const callData = await callsApi.get(callId);
        if (callData) {
          setLastCallStatus(prev => ({
            ...prev,
            status: callData.status,
            duration: callData.duration_ms,
            outcome: callData.outcome
          }));

          const finalStatuses = ['completed', 'failed', 'cancelled', 'no-answer', 'busy'];
          if (finalStatuses.includes(callData.status)) {
            setIsPollingStatus(false);
            return;
          }
        }
      } catch (err) {
        console.error('Error polling call status:', err);
      }

      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, 5000);
      } else {
        setIsPollingStatus(false);
      }
    };

    setTimeout(poll, 3000);
  };

  const handlePlaceTestCall = async ({ name, phone }) => {
    if (!name.trim() || !phone.trim()) {
      toast.error('Please enter both name and phone number');
      return false;
    }

    try {
      setPlacingCall(true);

      const lead = await leadsApi.create({
        first_name: name,
        last_name: '',
        phone,
        email: null,
        vehicle_interest: null,
        preferred_language: 'en',
        campaign_id: null
      });

      const campaigns = await campaignsApi.list();
      let campaignId = campaigns[0]?.id;

      if (!campaignId) {
        const config = await adminApi.testCall();

        if (!config.configured || !config.ready_for_calls) {
          throw new Error('Retell is not properly configured. Please check API key, Agent ID, and From Number in settings.');
        }

        const newCampaign = await campaignsApi.create({
          name: 'Test Campaign (Auto-created)',
          script_type: 'warm_lead',
          call_script: 'Hello, this is a test call from our AI Voice Calling system.',
          target_count: 100,
          call_window_start: '09:00',
          call_window_end: '20:00'
        });

        campaignId = newCampaign.id;
        toast.info('Created default test campaign automatically');
      }

      const call = await callsApi.initiate({
        lead_id: lead.id,
        campaign_id: campaignId,
        phone_number: phone
      });

      setLastCallStatus({
        id: call.id,
        status: call.status || 'ringing',
        phone,
        timestamp: new Date().toISOString()
      });

      toast.success(`Test Call Placed! Calling ${name} at ${phone}. Call ID: ${call.id.slice(0, 8)}...`);

      pollCallStatus(call.id);

      return true;
    } catch (err) {
      console.error('Error placing test call:', err);
      toast.error(err.message || 'Failed to place test call');
      setLastCallStatus({
        id: null,
        status: 'failed',
        phone,
        timestamp: new Date().toISOString(),
        error: err.message
      });
      return false;
    } finally {
      setPlacingCall(false);
    }
  };

  const tabs = [
    { id: 'production', label: 'Production' },
    { id: 'database', label: 'Database' },
    { id: 'compliance', label: 'Compliance' },
    { id: 'sheets', label: 'Sheets' },
    { id: 'reconcile', label: 'Reconcile' },
    { id: 'testcalls', label: 'Test calls' },
    canManageUsers && { id: 'team', label: 'Team' },
  ].filter(Boolean);

  if (!isPlatformAdmin && !canManageUsers) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Workspace"
          title="Admin"
          description="Production controls and operational tools."
        />
        <DataCard>
          <EmptyState
            title="Restricted area"
            description="You don't have access to the admin panel."
          />
        </DataCard>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Workspace"
        title="Admin"
        description="Production controls and operational tools."
        actions={
          isPlatformAdmin && (
            <button
              type="button"
              onClick={fetchProductionStatus}
              className="btn-2050 btn-secondary"
              disabled={loading}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          )
        }
      />

      <TabBar value={tab} onChange={setTab} items={tabs} />

      {errored && isPlatformAdmin ? (
        <DataCard>
          <EmptyState
            title="Unable to load right now."
            description="Production status is unavailable; click refresh to retry."
          />
        </DataCard>
      ) : (
        <>
          {tab === 'production' && (
            <div className="space-y-6">
              {isPlatformAdmin ? (
                loading ? (
                  <DataCard loading title="Production readiness" />
                ) : (
                  <>
                    <DataCard
                      title="Production readiness"
                      description="System configuration checks."
                      bodyClassName="p-0"
                    >
                      <div className="px-6 py-5">
                        <ProductionReadinessCard productionStatus={productionStatus} />
                      </div>
                    </DataCard>
                  </>
                )
              ) : (
                <DataCard>
                  <EmptyState title="Restricted" description="Platform admins only." />
                </DataCard>
              )}
            </div>
          )}

          {tab === 'database' && (
            isPlatformAdmin ? (
              loading ? (
                <DataCard loading title="Database statistics" />
              ) : (
                <DataCard
                  title="Database statistics"
                  description="Current record counts."
                  bodyClassName="p-0"
                >
                  <div className="px-6 py-5">
                    <DatabaseStatsGrid productionStatus={productionStatus} />
                  </div>
                </DataCard>
              )
            ) : (
              <DataCard>
                <EmptyState title="Restricted" description="Platform admins only." />
              </DataCard>
            )
          )}

          {tab === 'compliance' && (
            isPlatformAdmin ? (
              <div className="space-y-6">
                <DataCard
                  title="DNC list management"
                  description="Upload and manage do-not-call list."
                  bodyClassName="p-0"
                >
                  <div className="px-6 py-5">
                    <DNCListManagementCard
                      productionStatus={productionStatus}
                      onUploadDNC={handleRefreshDNC}
                      executing={executing}
                    />
                  </div>
                </DataCard>
                <DataCard
                  title="Compliance configuration"
                  description="Current enforcement settings."
                  bodyClassName="p-0"
                >
                  <div className="px-6 py-5">
                    <ComplianceConfigCard productionStatus={productionStatus} />
                  </div>
                </DataCard>
              </div>
            ) : (
              <DataCard>
                <EmptyState title="Restricted" description="Platform admins only." />
              </DataCard>
            )
          )}

          {tab === 'sheets' && (
            isPlatformAdmin ? (
              <DataCard
                title="Google Sheets sync"
                description="Pull leads from a Google Sheet."
                bodyClassName="p-0"
              >
                <div className="px-6 py-5">
                  <AdminSheetsSyncWidget />
                </div>
              </DataCard>
            ) : (
              <DataCard>
                <EmptyState title="Restricted" description="Platform admins only." />
              </DataCard>
            )
          )}

          {tab === 'reconcile' && (
            isPlatformAdmin ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <DataCard
                  title="Call reconciliation"
                  description='Fix phantom "in-progress" calls.'
                  bodyClassName="p-0"
                >
                  <div className="px-6 py-5">
                    <CallReconciliationCard
                      productionStatus={productionStatus}
                      onReconcile={handleReconcileCalls}
                      executing={executing}
                    />
                  </div>
                </DataCard>
                <DataCard
                  title="Data cleanup"
                  description="Remove test/seed data."
                  bodyClassName="p-0"
                >
                  <div className="px-6 py-5">
                    <DataCleanupCard
                      onCleanup={handleCleanupTestData}
                      executing={executing}
                    />
                  </div>
                </DataCard>
              </div>
            ) : (
              <DataCard>
                <EmptyState title="Restricted" description="Platform admins only." />
              </DataCard>
            )
          )}

          {tab === 'testcalls' && (
            isPlatformAdmin ? (
              <DataCard
                title="Quick test call"
                description="Place a test call instantly without creating leads."
                bodyClassName="p-0"
              >
                <div className="px-6 py-5">
                  <QuickTestCallCard
                    onPlaceCall={handlePlaceTestCall}
                    lastCallStatus={lastCallStatus}
                    isPollingStatus={isPollingStatus}
                    placingCall={placingCall}
                  />
                </div>
              </DataCard>
            ) : (
              <DataCard>
                <EmptyState title="Restricted" description="Platform admins only." />
              </DataCard>
            )
          )}

          {tab === 'team' && (
            <div className="space-y-6">
              {isPlatformAdmin && <DealersCard />}
              {canManageUsers && <UsersCard />}
              {isPlatformAdmin && <AgentTemplatesCard />}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Admin;
